/**
 * CallSidebarFollowUpEmails — bloque "Enviar correo de seguimiento" del
 * CallSidebar (visible en fase post_call).
 *
 * 3 tarjetas (llamada perdida · promo del mes · consulta gratis). Click en
 * una tarjeta envía la plantilla transaccional INMEDIATAMENTE — son
 * plantillas prehechas, sin personalización por parte del agente.
 *
 * Si el contacto no tiene email, las tarjetas quedan deshabilitadas con
 * mensaje — el backend devolvería 422 igualmente.
 *
 * Backend: ver `app/api/endpoints/crm/follow_up_emails.py` y
 * `app/templates/emails/follow_up_*.html` (migrofast).
 */
import { useState } from 'react';
import {
  CheckCircleIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

import { cn } from '@/lib/utils';
import {
  FOLLOW_UP_EMAIL_TEMPLATES,
  type FollowUpEmailTemplateId,
} from '@/types/followUpEmail';
import { useSendFollowUpEmail } from '@/hooks/useFollowUpEmails';

interface CallSidebarFollowUpEmailsProps {
  contactId: string | undefined;
  contactEmail: string | null | undefined;
}

export function CallSidebarFollowUpEmails({
  contactId,
  contactEmail,
}: CallSidebarFollowUpEmailsProps) {
  const sendMutation = useSendFollowUpEmail(contactId ?? '');
  const [pendingId, setPendingId] = useState<FollowUpEmailTemplateId | null>(
    null,
  );
  const [sentIds, setSentIds] = useState<Set<FollowUpEmailTemplateId>>(
    () => new Set(),
  );

  const hasEmail = Boolean(contactEmail?.trim());
  const disabled = !contactId || !hasEmail || sendMutation.isPending;
  const disabledReason = !contactId
    ? 'Sin contacto enlazado'
    : !hasEmail
      ? 'Sin email del contacto'
      : '';

  const handleClick = async (templateId: FollowUpEmailTemplateId) => {
    if (!contactId || !hasEmail) return;
    setPendingId(templateId);
    try {
      await sendMutation.mutateAsync({ template_id: templateId });
      setSentIds((prev) => {
        const next = new Set(prev);
        next.add(templateId);
        return next;
      });
    } catch {
      // El hook ya muestra toast.error — no hacemos nada más.
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section
      data-testid="call-sidebar-follow-up-emails"
      className="px-4 py-3 border-t border-gray-200"
    >
      <header className="mb-2 flex items-center gap-1.5">
        <EnvelopeIcon
          aria-hidden
          className="h-4 w-4 text-muted-foreground"
        />
        <h3 className="text-sm font-semibold text-gray-800">
          Enviar correo de seguimiento
        </h3>
      </header>

      {(!contactId || !hasEmail) && (
        <p
          className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
          role="status"
        >
          {disabledReason} — añade un email al contacto para habilitar el
          envío.
        </p>
      )}

      <div className="grid grid-cols-1 gap-2">
        {FOLLOW_UP_EMAIL_TEMPLATES.map((tpl) => {
          const isPending = pendingId === tpl.id;
          const isSent = sentIds.has(tpl.id);
          return (
            <button
              key={tpl.id}
              type="button"
              disabled={disabled}
              onClick={() => handleClick(tpl.id)}
              title={
                disabled
                  ? disabledReason
                  : isSent
                    ? 'Ya enviado — clic para reenviar'
                    : `Enviar "${tpl.label}" a ${contactEmail}`
              }
              data-testid={`follow-up-email-card-${tpl.id}`}
              aria-busy={isPending}
              className={cn(
                'flex items-start gap-2 rounded-lg border bg-white px-3 py-2 text-left transition-all',
                'hover:border-blue-300 hover:bg-blue-50/40 hover:shadow-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:shadow-none',
                isSent
                  ? 'border-emerald-300 bg-emerald-50/40'
                  : 'border-gray-200',
              )}
            >
              <span aria-hidden className="text-base shrink-0">
                {tpl.emoji}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-800">
                  {tpl.label}
                  {isSent && (
                    <CheckCircleIcon
                      aria-hidden
                      className="h-3.5 w-3.5 text-emerald-600"
                    />
                  )}
                </span>
                <span className="block text-[11px] text-muted-foreground line-clamp-2">
                  {tpl.description}
                </span>
              </span>
              <span
                aria-hidden
                className="ml-1 inline-flex h-6 w-6 items-center justify-center text-blue-500"
              >
                {isPending ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                ) : (
                  <PaperAirplaneIcon className="h-4 w-4" />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
